import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { isLoadingOrNull, JC_LOADING, JcLoading } from "jcsdk";
import { HoopsAuth } from "src/services/auth/models/HoopsAuth";
import { hoopsAuthService } from "src/services/auth/HoopsAuthService";

export type HoopsAuthState = HoopsAuth | null | JcLoading;

const HoopsAuthStateContext = createContext<HoopsAuthState | undefined>(undefined);
const HoopsAuthReloadContext = createContext<(() => Promise<HoopsAuth | null>) | undefined>(undefined);

/**
 * Subscribes once at the app root to Firebase auth state changes and exposes
 * the current state via context. Consumers read it via `useHoopsAuthState`.
 */
export const HoopsAuthProvider = (props: { children: React.ReactNode }): React.ReactElement => {
    const [auth, setAuth] = useState<HoopsAuthState>(JC_LOADING);

    useEffect(() => {
        return hoopsAuthService.onAuthChange(setAuth);
    }, []);

    const reloadAuth = useCallback(async (): Promise<HoopsAuth | null> => {
        const refreshed = await hoopsAuthService.reloadCurrentAuth();
        setAuth(refreshed);
        return refreshed;
    }, []);

    return (
        <HoopsAuthStateContext.Provider value={auth}>
            <HoopsAuthReloadContext.Provider value={reloadAuth}>
                {props.children}
            </HoopsAuthReloadContext.Provider>
        </HoopsAuthStateContext.Provider>
    );
};

/**
 * Returns the current auth state: the signed-in HoopsAuth, `null` if signed out,
 * or `JC_LOADING` while determining. Safe to call from any component under the
 * provider.
 */
export const useHoopsAuthState = (): HoopsAuthState => {
    const state = useContext(HoopsAuthStateContext);
    if (state === undefined) {
        throw new Error("useHoopsAuthState must be used within a HoopsAuthProvider");
    }
    return state;
};

/**
 * Returns the current HoopsAuth, asserting the user is signed in. Throws if the
 * auth state is loading or signed out. Use only inside components that are
 * guaranteed to render under a sign-in check (e.g. inside `<HoopsRequireAuth>`).
 */
export const useRequiredAuth = (): HoopsAuth => {
    const state = useHoopsAuthState();
    if (isLoadingOrNull(state)) {
        throw new Error("useRequiredAuth called without a signed-in user; wrap with <HoopsRequireAuth>");
    }
    return state;
};

/**
 * Returns a function that reloads the current Firebase user and re-emits the
 * provider state. Required to pick up out-of-band changes to `emailVerified`
 * after the user clicks the verification link in their inbox.
 */
export const useReloadHoopsAuth = (): (() => Promise<HoopsAuth | null>) => {
    const reload = useContext(HoopsAuthReloadContext);
    if (reload === undefined) {
        throw new Error("useReloadHoopsAuth must be used within a HoopsAuthProvider");
    }
    return reload;
};
